import type { ExtractedFileData, ImpactTransformResult } from "../types";
import type {
  CriticalInfrastructureImpact,
  DefenseCapacityImpact,
  InternationalObligationsImpact,
} from "@/types/impact-analysis";

export function transformImpact(
  extracted: ExtractedFileData
): ImpactTransformResult {
  const val = (fieldName: string) =>
    extracted.fields[fieldName]?.parsedValue ?? null;
  const str = (fieldName: string) => val(fieldName) as string | null;
  const num = (fieldName: string) => val(fieldName) as number | null;

  return {
    impactAnalysisData: {
      section1_mandatory: {
        is_sole_essential_provider: parseBooleanAnswer(
          str("is_sole_essential_provider")
        ),
        is_sole_essential_provider_explanation:
          str("is_sole_essential_provider_explanation") ?? undefined,
        formally_designated: parseBooleanAnswer(str("formally_designated")),
        formally_designated_explanation:
          str("formally_designated_explanation") ?? undefined,
      },
      section2_public_impact: {
        fundamental_rights: {
          personal_data_affected: num("personal_data_affected"),
          personal_data_affected_explanation:
            str("personal_data_affected_explanation") ?? undefined,
          public_service_access_lost: num("public_service_access_lost"),
          public_service_access_lost_explanation:
            str("public_service_access_lost_explanation") ?? undefined,
        },
        national_economy: {
          gdp_loss_over_threshold: parseBooleanAnswer(
            str("gdp_loss_over_threshold")
          ),
          gdp_loss_over_threshold_explanation:
            str("gdp_loss_explanation") ?? undefined,
          affects_other_sector_services: parseBooleanAnswer(
            str("affects_other_sector_services")
          ),
          affects_other_sector_services_explanation:
            str("affects_other_sector_explanation") ?? undefined,
          highest_sector_impact_percentage: num(
            "highest_sector_impact_percentage"
          ),
          highest_sector_impact_percentage_explanation:
            str("highest_sector_impact_explanation") ?? undefined,
          affects_critical_infrastructure: parseCriticalInfrastructure(
            str("affects_critical_infrastructure")
          ),
          affects_critical_infrastructure_explanation:
            str("affects_critical_infrastructure_explanation") ?? undefined,
        },
        health_life: {
          can_cause_death: parseBooleanAnswer(str("can_cause_death")),
          can_cause_death_explanation:
            str("can_cause_death_explanation") ?? undefined,
          chronic_illness_or_disability: num("chronic_illness_or_disability"),
          chronic_illness_or_disability_explanation:
            str("chronic_illness_explanation") ?? undefined,
          traumatic_injuries_or_health_impact: num(
            "traumatic_injuries_or_health_impact"
          ),
          traumatic_injuries_or_health_impact_explanation:
            str("traumatic_injuries_explanation") ?? undefined,
        },
        financial_impact: {
          people_financial_loss: num("people_financial_loss"),
          people_financial_loss_explanation:
            str("financial_loss_explanation") ?? undefined,
        },
        defense_security: {
          affects_defense_capacity: parseDefenseCapacity(
            str("affects_defense_capacity")
          ),
          affects_defense_capacity_explanation:
            str("defense_capacity_explanation") ?? undefined,
          generates_state_incapacity: parseBooleanAnswer(
            str("generates_state_incapacity")
          ),
          generates_state_incapacity_explanation:
            str("state_incapacity_explanation") ?? undefined,
          damages_state_reputation: parseBooleanAnswer(
            str("damages_state_reputation")
          ),
          damages_state_reputation_explanation:
            str("reputation_explanation") ?? undefined,
        },
      },
      section3_systemic_risk: {
        affects_other_sectors: parseBooleanAnswer(str("affects_other_sectors")),
        affects_other_sectors_explanation:
          str("affects_other_sectors_explanation") ?? undefined,
        affected_sectors_count: num("affected_sectors_count"),
        highest_impact_percentage: num("highest_impact_percentage"),
        highest_impact_percentage_explanation:
          str("impact_percentage_explanation") ?? undefined,
        affects_international_obligations: parseInternationalObligations(
          str("affects_international_obligations")
        ),
        affects_international_obligations_explanation:
          str("international_obligations_explanation") ?? undefined,
      },
    },
  };
}

function parseBooleanAnswer(value: string | null): boolean | null {
  if (!value || value === "Selectați") return null;
  const lower = value.toLowerCase().trim();
  if (lower === "da" || lower.startsWith("da,") || lower.startsWith("da "))
    return true;
  if (lower === "nu") return false;
  return null;
}

function parseCriticalInfrastructure(
  value: string | null
): CriticalInfrastructureImpact | null {
  if (!value || value === "Selectați") return null;
  const lower = value.toLowerCase();
  if (lower.includes("distrugeri")) return "destroyed";
  if (lower.includes("afectată") || lower.includes("afectat"))
    return "affected";
  if (lower === "nu") return "no";
  return null;
}

function parseDefenseCapacity(
  value: string | null
): DefenseCapacityImpact | null {
  if (!value || value === "Selectați") return null;
  const lower = value.toLowerCase();
  if (lower.includes("total")) return "destroyed";
  if (
    lower.includes("afectată") ||
    lower.includes("afectat") ||
    lower.includes("anumite")
  )
    return "affected";
  if (lower === "nu") return "no";
  return null;
}

function parseInternationalObligations(
  value: string | null
): InternationalObligationsImpact | null {
  if (!value || value === "Selectați") return null;
  const lower = value.toLowerCase();
  if (lower.includes("imposibilitate")) return "destroyed";
  if (lower.includes("afectată") || lower.includes("afectat"))
    return "affected";
  if (lower === "nu") return "no";
  return null;
}
